import React, { useState, useEffect } from 'react';
import Dashheader from '../Dashboard/Dashheader/Dashheader';
import api from './../../api.js';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { Table, Tag, Typography, DatePicker, Spin, Button } from 'antd';
import 'antd/dist/reset.css';
import { ArrowLeftOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';


const { Title } = Typography;
const { RangePicker } = DatePicker;

const UnpaidHistory = () => {
  const navigate = useNavigate();
  const [towerList, setTowerList] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dates, setDates] = useState([
    dayjs().startOf('month'),
    dayjs().add(0, 'month').endOf('month')
  ]);

  const fetchData = async () => {
    setLoading(true);
    const params = {
      startDate: dates[0]?.format('YYYY-MM-DD'),
      endDate: dates[1]?.format('YYYY-MM-DD'),
    };
    try {
      const response = await api.getUnpaidHistory(params);
      if (response.success && Array.isArray(response.data)) {
        setData(response.data);
      } else {
        setData([]);
      }
    } catch (error) {
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dates[0] && dates[1]) {
      fetchData();
    }
    // eslint-disable-next-line
  }, [dates]);

  const columns = [
    {
      title: 'User Name',
      dataIndex: 'userDetails',
      key: 'userDetails',
      render: user => user ? `${user.name} ${user.surname}` : '-',
      ellipsis: true,
      width: 150,
    },
    {
      title: 'Flat No',
      dataIndex: ['bookedFlatDetails', 'flatNo'],
      key: 'flatNo',
      ellipsis: true,
      width: 90,
    },
    {
      title: 'Tower Name',
      dataIndex: ['bookedFlatDetails', 'towerName'],
      key: 'towerName',
      ellipsis: true,
      width: 120,
    },
    {
      title: 'Schedule Name',
      dataIndex: 'scheme',
      key: 'scheduleName',
      width: 120,
      render: (scheme) => {
        if (scheme && scheme.length > 0) {
          const unpaidSchedules = scheme.filter(s => s.status === 'UNPAID' || s.status === 'PARTIALLY PAID');
          return unpaidSchedules.length > 0 ? unpaidSchedules[0].name || 'Schedule-1' : '-';
        }
        return '-';
      },
    },
    {
      title: 'Start Date',
      dataIndex: 'scheme',
      key: 'startDate',
      render: (scheme) => {
        if (scheme && scheme.length > 0) {
          const unpaidSchedules = scheme.filter(s => s.status === 'UNPAID' || s.status === 'PARTIALLY PAID');
          return unpaidSchedules.length > 0 && unpaidSchedules[0].startDate 
            ? dayjs(unpaidSchedules[0].startDate).format('DD MMM YYYY') 
            : '-';
        }
        return '-';
      },
      width: 120,
    },
    {
      title: 'Due Date',
      dataIndex: 'scheme',
      key: 'dueDate',
      render: (scheme) => {
        if (scheme && scheme.length > 0) {
          const unpaidSchedules = scheme.filter(s => s.status === 'UNPAID' || s.status === 'PARTIALLY PAID');
          return unpaidSchedules.length > 0 && unpaidSchedules[0].dueDate 
            ? dayjs(unpaidSchedules[0].dueDate).format('DD MMM YYYY') 
            : '-';
        }
        return '-';
      },
      width: 120,
    },
    {
      title: 'Total Scheme Amount',
      dataIndex: 'totalSchemeAmount',
      key: 'totalSchemeAmount',
      render: amount => amount ? amount.toLocaleString() : '-',
      width: 140,
    },
    {
      title: 'Paid (w/o GST)',
      dataIndex: 'paidWithoutGstAmount',
      key: 'paidWithoutGstAmount',
      render: val => val !== undefined && val !== null ? val.toLocaleString() : '-',
      width: 120,
    },
    {
      title: 'Paid GST',
      dataIndex: 'paidGstAmount',
      key: 'paidGstAmount',
      render: val => val !== undefined && val !== null ? val.toLocaleString() : '-',
      width: 100,
    },
    {
      title: 'Total Paid (with GST)',
      dataIndex: 'totalPaidWithGst',
      key: 'totalPaidWithGst',
      render: val => val !== undefined && val !== null ? val.toLocaleString() : '-',
      width: 140,
    },
    {
      title: 'Balance (w/o GST)',
      dataIndex: 'balanceWithoutGst',
      key: 'balanceWithoutGst',
      render: val => val ? val.toLocaleString() : '-',
      width: 140,
    },
    {
      title: 'Status',
      dataIndex: 'scheme',
      key: 'status',
      render: (scheme) => {
        if (scheme && scheme.length > 0) {
          const unpaidSchedules = scheme.filter(s => s.status === 'UNPAID' || s.status === 'PARTIALLY PAID');
          const status = unpaidSchedules.length > 0 ? unpaidSchedules[0].status : 'PAID';
          return (
            <Tag 
              color={
                status === 'UNPAID' ? 'orange' : 
                status === 'PARTIALLY PAID' ? 'blue' : 'green'
              } 
              style={{ fontWeight: 600, fontSize: 14, borderRadius: 8, padding: '2px 16px' }}
            >
              {status}
            </Tag>
          );
        }
        return (
          <Tag color="green" style={{ fontWeight: 600, fontSize: 14, borderRadius: 8, padding: '2px 16px' }}>
            PAID
          </Tag>
        );
      },
      width: 120,
    },
    {
      title: 'Action',
      key: 'action',
      render: (text, record) => (
        <span>
          <a onClick={() => handleViewDetails(record.userDetails._id)} style={{ color: '#1a237e', fontWeight: 600 }}>View Details</a>
        </span>
      ),
      width: 120,
    },
  ];

  const handleViewDetails = (userId) => {
    console.log("Navigating to:", `/dashboard/getcustomertracking/${userId}`); 
    navigate(`/dashboard/getcustomertracking/${userId}`);
  };

  const handleDownloadExcel = () => {
  if (!data || data.length === 0) return;

  const excelData = data.map(item => {
    const unpaidSchedule = item.scheme?.find(
      s => s.status === 'UNPAID' || s.status === 'PARTIALLY PAID'
    );

    return {
      'User Name': item.userDetails
        ? `${item.userDetails.name} ${item.userDetails.surname}`
        : '-',
      'Flat No': item.bookedFlatDetails?.flatNo || '-',
      'Tower Name': item.bookedFlatDetails?.towerName || '-',
      'Schedule Name': unpaidSchedule?.name || '-',
      'Start Date': unpaidSchedule?.startDate
        ? dayjs(unpaidSchedule.startDate).format('DD MMM YYYY')
        : '-',
      'Due Date': unpaidSchedule?.dueDate
        ? dayjs(unpaidSchedule.dueDate).format('DD MMM YYYY')
        : '-',
      'Total Scheme Amount': item.totalSchemeAmount || 0,
      'Paid (w/o GST)': item.paidWithoutGstAmount || 0,
      'Paid GST': item.paidGstAmount || 0,
      'Total Paid (with GST)': item.totalPaidWithGst || 0,
      'Balance (w/o GST)': item.balanceWithoutGst || 0,
      'Status': unpaidSchedule?.status || 'PAID',
    };
  });

  const worksheet = XLSX.utils.json_to_sheet(excelData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Unpaid History');

  XLSX.writeFile(workbook, 'Unpaid_History.xlsx');
};


  return (
    <>
      <Dashheader />

      <Button onClick={() => navigate('/dashboard/reports')} style={{ marginBottom: 16, marginTop: 20, background: '#1890ff', color: '#fff', border: 'none' }}>
        <ArrowLeftOutlined style={{ marginRight: 6 }} />
        Back To reports
      </Button>

      <Button
  type="primary"
  style={{ marginLeft: 12 }}
  onClick={handleDownloadExcel}
>
  Download Excel
</Button>

      <div style={{ width: '100vw', margin: 0, overflowX: 'hidden' }}>
        <div>
          <Title level={2} style={{ margin: '40px 0 24px 0', color: '#1a237e', fontWeight: 800, letterSpacing: 1, textAlign: 'center' }}>Unpaid History</Title>
          <div style={{ marginBottom: 28, display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'center' }}>
            <span style={{ fontWeight: 500, fontSize: 14 }}>Select Date Range:</span>
            <RangePicker
              value={dates}
              onChange={val => setDates(val)}
              allowClear={false}
              style={{ minWidth: 200, borderRadius: 8, border: '1px solid #bdbdbd' }}
              format="DD MMM YYYY"
              size="large"
            />
          </div>
          <Spin spinning={loading} tip="Loading...">
            <div style={{ width: '100%', overflowX: 'auto', overflowY: 'visible' }}>
              <Table
                columns={columns}
                dataSource={data}
                rowKey="_id"
                bordered={false}
                pagination={{ pageSize: 8, showSizeChanger: false }}
                style={{ background: '#fff', borderRadius: 0, minWidth: 1400, boxShadow: 'none', border: 'none' }}
                scroll={{ x: 1600 }}
                size="middle"
              />
            </div>
          </Spin>
        </div>
      </div>
    </>
  );
};

export default UnpaidHistory;
